// IndependentLoginPage.js - Login page for independent agents
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services';
import { useAuth } from '../../contexts/AuthContext';
import AuthShell, { AuthField, AuthErrorBanner, AuthPrimaryButton, AuthPasswordInput, AUTH_INPUT } from './AuthShell';

const IndependentLoginPage = () => {
  const navigate = useNavigate();
  const { login: authLogin, checkAuth, user } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // 2FA state
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [verificationMethod, setVerificationMethod] = useState('email');
  const [verificationCode, setVerificationCode] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [maskedPhone, setMaskedPhone] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        const response = await authLogin({
          email: formData.email,
          password: formData.password,
          independentAgent: true
        });
        
        // Check if 2FA is required - extract from nested data structure
        const responseData = response.data?.data || response.data || response;
        if (responseData.requiresTwoFactor) {
          setRequires2FA(true);
          setTempToken(responseData.tempToken);
          setMaskedEmail(responseData.maskedEmail);
          setMaskedPhone(responseData.maskedPhone);
          setLoading(false);
          return;
        }
        
        // No 2FA required, proceed with normal login
        await checkAuth();
        
        // Extract user from response (backend returns { success, data: { token, user } })
        const responseUser = response.data?.data?.user || response.data?.user || response.user;
        
        // Wait for state update, then use user from response or context
        setTimeout(() => {
          const currentUser = responseUser || user;
          if (!currentUser) {
            console.error('No user data available after login');
            setError('Login succeeded but user data not available. Please try again.');
            return;
          }
          
          const userRole = currentUser.role;
          console.log('Navigating based on user role:', userRole, 'status:', currentUser.status);
          
          // MIGRATION: Independent agents now use the same dashboard as agency agents
          if (userRole === 'independent_agent') {
            console.log('Independent agent detected, redirecting to agent dashboard');
            navigate('/agent/dashboard');
            return;
          }
          
          // Check for commission-only agents
          if (userRole === 'agent' && currentUser.status === 'commission_only') {
            console.log('Commission-only agent detected, redirecting to commissions-only page');
            navigate('/agent/commissions-only');
            return;
          }
          
          // Navigate based on user role
          if (userRole === 'agency_admin') {
            navigate('/agency/dashboard');
          } else if (userRole === 'agent') {
            navigate('/agent/dashboard');
          } else if (userRole === 'super_admin') {
            navigate('/admin/dashboard');
          } else {
            navigate('/agent/dashboard'); // Default to agent dashboard
          }
        }, 200);
      } else {
        // Registration would go to a different page for subscription
        navigate('/register/independent');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleSend2FA = async () => {
    setError('');
    setLoading(true);

    try {
      await authService.sendIndependent2FA(tempToken, verificationMethod);
      setError(''); // Clear any previous errors
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authService.verifyIndependent2FA(tempToken, verificationCode);
      
      // Store token and user data
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      await checkAuth();
      
      // Navigate to unified agent dashboard (independent agents use same dashboard as agency agents)
      navigate('/agent/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-[var(--foreground)]" style={{ fontFamily: 'var(--font-display)' }}>
          Independent Agent Portal
        </h2>
        <p className="text-sm text-[var(--muted-foreground)] mt-1">
          {requires2FA ? 'Verify your identity to continue' : isLogin ? 'Sign in to your account' : 'Create your account'}
        </p>
      </div>

      <AuthErrorBanner>{error}</AuthErrorBanner>

      {requires2FA ? (
        <form onSubmit={handleVerify2FA} className="space-y-4 mt-4">
          <div className="text-center mb-2">
            <div className="w-14 h-14 rounded-2xl mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: 'var(--primary)' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
            <h3 className="text-sm font-bold text-[var(--foreground)]">Two-Factor Authentication</h3>
            <p className="text-xs text-[var(--muted-foreground)] mt-1">
              To view payment details, please verify your identity using two-factor authentication.
            </p>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-bold uppercase tracking-widest text-[var(--muted-foreground)]">Choose verification method</label>
            <label className="flex items-center p-3 border border-[var(--border)] rounded-xl cursor-pointer hover:bg-[var(--muted)] transition-all">
              <input
                type="radio"
                name="method"
                value="email"
                checked={verificationMethod === 'email'}
                onChange={(e) => setVerificationMethod(e.target.value)}
                className="h-4 w-4"
                style={{ accentColor: 'var(--primary)' }}
              />
              <span className="ml-3 text-sm text-[var(--foreground)]">
                Send code via Email {maskedEmail && `(${maskedEmail})`}
              </span>
            </label>
            {maskedPhone && (
              <label className="flex items-center p-3 border border-[var(--border)] rounded-xl cursor-pointer hover:bg-[var(--muted)] transition-all">
                <input
                  type="radio"
                  name="method"
                  value="sms"
                  checked={verificationMethod === 'sms'}
                  onChange={(e) => setVerificationMethod(e.target.value)}
                  className="h-4 w-4"
                  style={{ accentColor: 'var(--primary)' }}
                />
                <span className="ml-3 text-sm text-[var(--foreground)]">
                  Send code via SMS ({maskedPhone})
                </span>
              </label>
            )}
          </div>

          <button
            type="button"
            onClick={handleSend2FA}
            disabled={loading}
            className="w-full py-2.5 rounded-xl text-sm font-semibold border border-[var(--border)] text-[var(--foreground)] hover:bg-[var(--muted)] transition-all disabled:opacity-50"
          >
            {loading ? 'Sending...' : 'Send Verification Code'}
          </button>

          <AuthField label="Verification Code" htmlFor="verificationCode">
            <input
              id="verificationCode"
              name="verificationCode"
              type="text"
              maxLength="6"
              placeholder="Enter 6-digit code"
              required
              className={`${AUTH_INPUT} text-center text-xl tracking-widest`}
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            />
          </AuthField>

          <AuthPrimaryButton type="submit" loading={loading} loadingLabel="Verifying…" disabled={verificationCode.length !== 6}>
            Verify & Sign In
          </AuthPrimaryButton>

          <div className="text-center">
            <button
              type="button"
              onClick={() => {
                setRequires2FA(false);
                setTempToken('');
                setVerificationCode('');
                setError('');
                setFormData({ ...formData, email: '', password: '' });
                setLoading(false);
              }}
              className="text-xs text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors"
            >
              ← Back to login
            </button>
          </div>
        </form>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <div className="grid grid-cols-2 gap-3">
              <AuthField label="First Name" htmlFor="firstName">
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  required={!isLogin}
                  className={AUTH_INPUT}
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                />
              </AuthField>
              <AuthField label="Last Name" htmlFor="lastName">
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  required={!isLogin}
                  className={AUTH_INPUT}
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                />
              </AuthField>
            </div>
          )}

          <AuthField label="Email Address" htmlFor="email">
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              placeholder="you@example.com"
              className={AUTH_INPUT}
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
          </AuthField>

          <AuthField label="Password" htmlFor="password">
            <AuthPasswordInput
              id="password"
              name="password"
              autoComplete={isLogin ? 'current-password' : 'new-password'}
              required
              placeholder="••••••••"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              showPassword={showPassword}
              setShowPassword={setShowPassword}
            />
          </AuthField>

          {!isLogin && (
            <AuthField label="Phone Number" htmlFor="phone">
              <input
                id="phone"
                name="phone"
                type="tel"
                autoComplete="tel"
                required={!isLogin}
                className={AUTH_INPUT}
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </AuthField>
          )}

          <AuthPrimaryButton type="submit" loading={loading} loadingLabel="Processing…">
            {isLogin ? 'Sign In' : 'Continue to Subscription'}
          </AuthPrimaryButton>
        </form>
      )}

      {!requires2FA && (
        <>
          <div className="relative flex items-center gap-3 py-1 mt-6">
            <div className="flex-1 h-px bg-[var(--border)]" />
            <span className="text-[10px] font-semibold uppercase tracking-widest text-[var(--muted-foreground)]">
              {isLogin ? "Don't have an account?" : 'Already have an account?'}
            </span>
            <div className="flex-1 h-px bg-[var(--border)]" />
          </div>

          <button
            type="button"
            onClick={() => {
              setIsLogin(!isLogin);
              setError('');
              setFormData({ email: '', password: '', confirmPassword: '', firstName: '', lastName: '', phone: '' });
            }}
            className="w-full mt-4 py-2.5 rounded-xl text-sm font-semibold border border-[var(--border)] text-[var(--muted-foreground)] hover:text-[var(--foreground)] hover:bg-[var(--muted)] transition-all"
          >
            {isLogin ? 'Create new account' : 'Sign in instead'}
          </button>
        </>
      )}
    </AuthShell>
  );
};

export default IndependentLoginPage;
